<script lang="ts">
    // Zgrid2 — tabela de dados. Independente do Zgrid (zgrid.svelte): modelos em zgrid2.ts, célula em
    // zgrid2_cell.svelte, popups em zgrid2_popup.svelte e todo o CSS dentro dos próprios componentes (nada vem
    // do css_z.css). Retrocompatível com a configuração do Zgrid: um GridClass montado com zgrid.ts funciona aqui
    // sem mudança — basta trocar <Zgrid> por <Zgrid2>. Ver o cabeçalho de zgrid2.ts.
    import { onDestroy, tick } from 'svelte'
    import { slide } from 'svelte/transition'
    import { flip } from 'svelte/animate'
    import Fa from 'svelte-fa'
    import {
        faCalendarAlt,
        faCheck,
        faChevronDown,
        faFileExport,
        faFilter,
        faGripVertical,
        faPrint,
        faShareAlt,
        faSort,
        faSortDown,
        faSortUp,
        faTimes,
    } from '@fortawesome/free-solid-svg-icons'
    import type { SharePageEntity } from '../../../modules/toolsApi/share-pages/share-page.entity'
    import Popover from '../popover.svelte'
    import SharePageLink from '../share-page-link.svelte'
    import Zblock from './zblock.svelte'
    import Zgrid2Cell from './zgrid2_cell.svelte'
    import Zgrid2Popup from './zgrid2_popup.svelte'
    import DateSelect from '../date-select.svelte'
    import dayjs from 'dayjs'
    import {
        GridClass,
        GridCol,
        GridColAlignEnum,
        GridColTypeEnum,
        GridColVisibilityEnum,
        GridItemShow,
        GridLine,
        GridSlot,
        compareSortValues,
        csvValueOf,
        filterKeysOf,
        fingerprintOf,
        formatContent,
        isInteractiveType,
        isNumericType,
        normalizeText,
        searchTextOf,
        sortValueOf,
        toNumber,
    } from './zgrid2'
    import type { GridBox, GridButton } from './zgrid2'

    // o grid recebido da tela e o grid exibido são variáveis separadas de propósito — ver receiveGrid()
    let incomingGrid: GridClass
    export { incomingGrid as grid }
    export let shared: boolean = false
    export { className as class }
    let className = ''

    // ###################################################################### recebimento do grid (no-op sem mudança)
    // a tela costuma remontar o GridClass a cada recarga (new GridClass() ou grid = grid). o grid exibido só é trocado
    // quando algum dado mudou de fato; com exatamente os mesmos dados é no-op — ordenação, filtros, linha marcada,
    // checkboxes e popups abertos ficam como estão. a comparação ignora callbacks (ver fingerprintOf em zgrid2.ts).
    // mudanças feitas aqui dentro (checkbox, act segmentado) usam "grid = grid" e não passam por esta porta.
    // PRECISA ser o primeiro $: do componente: o Svelte roda os $: uma vez por rodada, na ordem do arquivo, e um $:
    // que já passou não é reavaliado quando algo que ele lê muda depois dele (ex.: colunas visíveis calculadas antes
    // de onGridChanged carregar a escolha salva — a escolha não aparecia ao voltar pra tela).
    let grid: GridClass = null
    let lastFingerprint: string = null
    $: receiveGrid(incomingGrid)

    function receiveGrid(incoming: GridClass) {
        if (!incoming) {
            grid = incoming
            lastFingerprint = null
            return
        }
        const fingerprint = fingerprintOf(incoming)
        if (grid && fingerprint === lastFingerprint) return
        lastFingerprint = fingerprint
        grid = incoming
        // GridClass novo (outro _updatedAt): reaplica estado. mesmo objeto com dados mudados (grid = grid na tela) só redesenha
        if (incoming._updatedAt !== lastUpdate) onGridChanged(incoming)
    }

    type ColumnView = { col: GridCol; index: number; key: string }
    type FilterOption = { key: string; label: string; count: number; order: number | string }

    const isMobile = screen.width <= 900
    // seleção em massa (col.type = checkbox) só para usuário super, igual ao Zgrid. optional chaining de propósito:
    // o zgrid2 também é montado na página compartilhada, onde não há login
    const isSuperUser = !!window.store?.token?.permissions?.super
    $: bulkEnabled = isSuperUser && !shared && !isMobile

    // chave de coluna pra ordenação e filtro: posição + título, pra não confundir colunas se o grid for recriado
    function colKey(col: GridCol, index: number): string {
        return `${index}|${col?.content || ''}`
    }

    function indexOfKey(source: GridClass, key: string): number {
        const index = Number(String(key).split('|')[0])
        return source?.cols[index] && colKey(source.cols[index], index) === key ? index : -1
    }

    function viewsOf(source: GridClass): ColumnView[] {
        return source ? source.cols.map((col, index) => ({ col, index, key: colKey(col, index) })) : []
    }

    function plural(count: number, singular: string, pluralForm: string): string {
        return `${count} ${count === 1 ? singular : pluralForm}`
    }

    // ###################################################################### colunas: exibição e ordem (botão "Colunas")
    // personalização do usuário em relação ao padrão das colunas, pelo título de cada uma:
    // - order: títulos na ordem escolhida (vazio = ordem original)
    // - visible: só as colunas cuja exibição difere do col.visibility (coluna fixed nunca entra)
    // fica no localStorage (ver storageKeyOf). o popup edita um rascunho; só "Aplicar" muda a tabela e salva.
    type ColumnPrefs = { order: string[]; visible: Record<string, boolean> }
    type ColumnDraftItem = { key: string; title: string; fixed: boolean; visible: boolean }
    const EMPTY_PREFS: ColumnPrefs = { order: [], visible: {} }
    let columnPrefs: ColumnPrefs = EMPTY_PREFS

    // colunas que aparecem na lista do popup. as demais (checkbox, botões sem título, agrupamento) não saem do lugar
    function isToggleable(col: GridCol): boolean {
        if (!col || col.show === false || col.isGroup) return false
        if (col.type === GridColTypeEnum.checkbox) return false
        if (isMobile && col.mobile === GridItemShow.hide) return false
        return !!col.content
    }

    function isFixedColumn(col: GridCol): boolean {
        return col?.visibility === GridColVisibilityEnum.fixed
    }

    function defaultVisibleOf(col: GridCol): boolean {
        return col.visibility !== GridColVisibilityEnum.hidden
    }

    // exibição escolhida (ou a padrão) de uma coluna da lista, antes das regras de dispositivo e permissão
    function chosenVisibleOf(col: GridCol, prefs: ColumnPrefs): boolean {
        if (isFixedColumn(col)) return true
        if (Object.prototype.hasOwnProperty.call(prefs.visible, col.content)) return prefs.visible[col.content]
        return defaultVisibleOf(col)
    }

    function isColumnVisible(col: GridCol, prefs: ColumnPrefs, bulk: boolean): boolean {
        if (!col || col.isGroup || col.show === false) return false
        if (col.type === GridColTypeEnum.checkbox) return bulk
        if (shared && isInteractiveType(col.type)) return false
        if (isMobile && col.mobile === GridItemShow.hide) return false
        return isToggleable(col) ? chosenVisibleOf(col, prefs) : defaultVisibleOf(col)
    }

    // todas as colunas na ordem de exibição: as da lista seguem prefs.order, ocupando entre si as posições que já
    // tinham; as demais não saem do lugar (checkbox segue na frente, botões no fim). coluna que não está na ordem
    // salva (nova na tela) entra na posição original dela.
    function orderedViews(source: GridClass, prefs: ColumnPrefs): ColumnView[] {
        const views = viewsOf(source)
        const listed = views.filter((view) => isToggleable(view.col))
        if (prefs.order.length === 0 || listed.length === 0) return views
        const known = prefs.order.map((title) => listed.find((view) => view.col.content === title)).filter(Boolean)
        const ordered = known.filter((view, position) => known.indexOf(view) === position)
        listed.forEach((view, position) => {
            if (!ordered.includes(view)) ordered.splice(Math.min(position, ordered.length), 0, view)
        })
        let cursor = 0
        return views.map((view) => (isToggleable(view.col) ? ordered[cursor++] : view))
    }

    // chave da escolha de colunas no localStorage: grid.id quando a tela informa; senão uma chave automática pelo
    // título do grid (sem título, pelos títulos das colunas) — assim uma tela que só trocou <Zgrid> por <Zgrid2> também
    // lembra a escolha ao sair e voltar. no link compartilhado e na impressão não há escolha a lembrar.
    let columnsStorageKey: string = undefined

    function storageKeyOf(source: GridClass): string {
        if (!source || shared) return null
        if (source.id) return `zgrid2_columns_${source.id}`
        const name = source.title || source.cols.map((col) => col.content || '').join(',')
        return name ? `zgrid2_columns_auto_${name}` : null
    }

    function loadColumnPrefs(key: string): ColumnPrefs {
        if (!key) return EMPTY_PREFS
        try {
            const saved = JSON.parse(localStorage.getItem(key) || 'null')
            if (!saved || typeof saved !== 'object') return EMPTY_PREFS
            // formato anterior à reordenação: só exibição, { "Título": true/false }
            if (!Array.isArray(saved.order) && !saved.visible) return { order: [], visible: saved }
            return { order: Array.isArray(saved.order) ? saved.order : [], visible: saved.visible || {} }
        } catch {
            return EMPTY_PREFS
        }
    }

    function isEmptyPrefs(prefs: ColumnPrefs): boolean {
        return prefs.order.length === 0 && Object.keys(prefs.visible).length === 0
    }

    function saveColumnPrefs() {
        const key = columnsStorageKey
        if (!key) return
        try {
            // igual ao padrão = sem personalização: remove em vez de salvar
            if (isEmptyPrefs(columnPrefs)) localStorage.removeItem(key)
            else localStorage.setItem(key, JSON.stringify(columnPrefs))
        } catch {
            // localStorage indisponível (modo privado, cota): a escolha vale só nesta sessão
        }
    }

    // ------------------------------------------------ rascunho do popup
    let columnsDraft: ColumnDraftItem[] = []
    let columnsList: HTMLElement = null
    let draggingKey: string = null

    function draftOf(source: GridClass, prefs: ColumnPrefs): ColumnDraftItem[] {
        return orderedViews(source, prefs)
            .filter((view) => isToggleable(view.col))
            .map((view) => ({ key: view.key, title: view.col.content, fixed: isFixedColumn(view.col), visible: chosenVisibleOf(view.col, prefs) }))
    }

    function openColumns(event: MouseEvent) {
        columnsDraft = draftOf(grid, columnPrefs)
        columnsAnchor = event.currentTarget as HTMLElement
    }

    function toggleDraftColumn(key: string) {
        columnsDraft = columnsDraft.map((item) => (item.key === key && !item.fixed ? { ...item, visible: !item.visible } : item))
    }

    // "Restaurar" volta o rascunho ao padrão; ao aplicar, a personalização salva é removida
    function restoreColumnsDraft() {
        columnsDraft = draftOf(grid, EMPTY_PREFS)
    }

    // personalização que o rascunho representa: só o que difere do padrão
    function prefsOfDraft(draft: ColumnDraftItem[]): ColumnPrefs {
        const defaults = draftOf(grid, EMPTY_PREFS)
        const visible: Record<string, boolean> = {}
        for (const item of draft) {
            const original = defaults.find((entry) => entry.key === item.key)
            if (!item.fixed && original && original.visible !== item.visible) visible[item.title] = item.visible
        }
        const sameOrder = draft.length === defaults.length && draft.every((item, position) => defaults[position].key === item.key)
        return { order: sameOrder ? [] : draft.map((item) => item.title), visible }
    }

    function applyColumns() {
        columnPrefs = prefsOfDraft(columnsDraft)
        saveColumnPrefs()
        closePopups()
    }

    function moveDraftColumn(key: string, target: number) {
        const from = columnsDraft.findIndex((item) => item.key === key)
        const to = Math.max(0, Math.min(target, columnsDraft.length - 1))
        if (from < 0 || from === to) return
        const next = columnsDraft.filter((item) => item.key !== key)
        next.splice(to, 0, columnsDraft[from])
        columnsDraft = next
    }

    // reordenar arrastando pelo pegador (pointer events: mouse e toque). o item troca de lugar no rascunho durante o
    // arraste e o animate:flip suaviza. listeners na window, não no pegador: o nó do item é movido no DOM a cada troca,
    // o que derrubaria um pointer capture. posições por offsetTop, que ignora a transformação da animação.
    function startColumnDrag(event: PointerEvent, key: string) {
        if (event.pointerType === 'mouse' && event.button !== 0) return
        event.preventDefault()
        draggingKey = key
        const pointerId = event.pointerId
        const move = (moveEvent: PointerEvent) => {
            if (moveEvent.pointerId !== pointerId || !columnsList || !draggingKey) return
            const listRect = columnsList.getBoundingClientRect()
            // perto das bordas, rola a lista
            if (moveEvent.clientY < listRect.top + 24) columnsList.scrollTop -= 8
            else if (moveEvent.clientY > listRect.bottom - 24) columnsList.scrollTop += 8
            const y = moveEvent.clientY - listRect.top + columnsList.scrollTop
            let target = 0
            for (const element of Array.from(columnsList.querySelectorAll<HTMLElement>('[data-column-key]'))) {
                if (element.dataset.columnKey === draggingKey) continue
                if (y > element.offsetTop + element.offsetHeight / 2) target++
            }
            moveDraftColumn(draggingKey, target)
        }
        const end = (endEvent: PointerEvent) => {
            if (endEvent.pointerId !== pointerId) return
            draggingKey = null
            window.removeEventListener('pointermove', move)
            window.removeEventListener('pointerup', end)
            window.removeEventListener('pointercancel', end)
        }
        window.addEventListener('pointermove', move)
        window.addEventListener('pointerup', end)
        window.addEventListener('pointercancel', end)
    }

    // teclado: seta pra cima/baixo no pegador move a coluna (e o foco acompanha o pegador)
    async function onGripKeydown(event: KeyboardEvent, key: string) {
        if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return
        event.preventDefault()
        const from = columnsDraft.findIndex((item) => item.key === key)
        moveDraftColumn(key, from + (event.key === 'ArrowUp' ? -1 : 1))
        await tick()
        const items = Array.from(columnsList?.querySelectorAll<HTMLElement>('[data-column-key]') || [])
        items
            .find((element) => element.dataset.columnKey === key)
            ?.querySelector<HTMLElement>('.z2grip')
            ?.focus()
    }

    $: columns = orderedViews(grid, columnPrefs).filter((view) => isColumnVisible(view.col, columnPrefs, bulkEnabled))
    $: toggleableColumns = viewsOf(grid).filter((view) => isToggleable(view.col))
    $: draftVisibleCount = columnsDraft.filter((item) => item.visible).length
    $: draftIsDefault = isEmptyPrefs(prefsOfDraft(columnsDraft))

    // ###################################################################### estado de busca, filtro e ordenação
    let searchText = ''
    let query = ''
    let sortKey: string = null
    let sortDirection: 'asc' | 'desc' = 'asc'
    // valores marcados por coluna (colKey -> chaves de filterKeysOf). vazio = sem filtro
    let filters: Record<string, string[]> = {}

    // grid novo (new GridClass() gera outro _updatedAt): mantém busca, ordenação e os filtros que ainda fazem
    // sentido com os dados novos — trocar o período num act segmentado não deve apagar o que o usuário montou.
    // chamado por receiveGrid(), no primeiro $: do componente (ver o comentário lá).
    let lastUpdate: any = undefined

    function onGridChanged(source: GridClass) {
        lastUpdate = source._updatedAt
        const storageKey = storageKeyOf(source)
        if (storageKey !== columnsStorageKey) {
            columnsStorageKey = storageKey
            columnPrefs = loadColumnPrefs(storageKey)
        }
        const next: Record<string, string[]> = {}
        source.cols.forEach((col, index) => {
            const key = colKey(col, index)
            if (col.filter?.length) {
                next[key] = col.filter.map(String)
                return
            }
            const previous = filters[key]
            if (!previous?.length) return
            const available = new Set<string>()
            for (const line of source.lines) {
                for (const value of filterKeysOf(col, line?.slots?.[index])) available.add(value)
            }
            const kept = previous.filter((value) => available.has(value))
            if (kept.length > 0) next[key] = kept
        })
        filters = next
        if (sortKey && indexOfKey(source, sortKey) < 0) sortKey = null
        remapSelectedLine(source)
        closePopups()
    }

    let searchTimer: ReturnType<typeof setTimeout> = null
    function onSearchInput() {
        clearTimeout(searchTimer)
        searchTimer = setTimeout(() => (query = searchText), 250)
    }

    function clearSearch() {
        clearTimeout(searchTimer)
        searchText = ''
        query = ''
    }

    function clearSearchAndFilters() {
        clearSearch()
        filters = {}
    }

    onDestroy(() => clearTimeout(searchTimer))

    function isSortable(col: GridCol): boolean {
        if (col.sortable !== null && col.sortable !== undefined) return col.sortable
        return !isInteractiveType(col.type)
    }

    function isFilterable(col: GridCol): boolean {
        if (isMobile) return false
        if (col.filterable !== null && col.filterable !== undefined) return col.filterable
        return !isInteractiveType(col.type) && !isNumericType(col.type)
    }

    // asc -> desc -> sem ordenação
    function toggleSort(key: string) {
        if (sortKey !== key) {
            sortKey = key
            sortDirection = 'asc'
        } else if (sortDirection === 'asc') {
            sortDirection = 'desc'
        } else {
            sortKey = null
            sortDirection = 'asc'
        }
    }

    function groupKeyOf(line: GridLine, index: number): string {
        return JSON.stringify(line?.slots[index]?.content ?? null)
    }

    // não altera grid.lines: busca, filtro e ordenação geram uma lista nova a cada mudança
    function computeRows(source: GridClass, term: string, activeFilters: Record<string, string[]>, key: string, direction: 'asc' | 'desc'): GridLine[] {
        const normalizedTerm = normalizeText(term)
        const filterList = Object.entries(activeFilters)
            .map(([filterKey, values]) => ({ index: indexOfKey(source, filterKey), values: new Set(values) }))
            .filter((item) => item.index >= 0 && item.values.size > 0)

        let list = source.lines.filter((line) => {
            if (!line || line.visible === false) return false
            for (const item of filterList) {
                const keys = filterKeysOf(source.cols[item.index], line.slots[item.index])
                if (!keys.some((value) => item.values.has(value))) return false
            }
            if (normalizedTerm) {
                const found = line.slots.some(
                    (slot, index) => source.cols[index] && normalizeText(searchTextOf(source.cols[index], slot)).includes(normalizedTerm),
                )
                if (!found) return false
            }
            return true
        })

        const sortIndex = key ? indexOfKey(source, key) : -1
        if (sortIndex >= 0) {
            const col = source.cols[sortIndex]
            const factor = direction === 'desc' ? -1 : 1
            const values = new Map(list.map((line) => [line, sortValueOf(col, line.slots[sortIndex])]))
            list.sort((a, b) => {
                const valueA = values.get(a)
                const valueB = values.get(b)
                // vazios sempre no fim, nas duas direções
                if (valueA === null || valueB === null) return compareSortValues(valueA, valueB)
                return factor * compareSortValues(valueA, valueB)
            })
            // com agrupamento (col.isGroup), os grupos ficam na ordem original e a ordenação vale dentro de cada um
            const groupIndex = source.cols.findIndex((item) => item.isGroup)
            if (groupIndex >= 0) {
                const order = new Map<string, number>()
                for (const line of source.lines) {
                    const group = groupKeyOf(line, groupIndex)
                    if (!order.has(group)) order.set(group, order.size)
                }
                list.sort((a, b) => order.get(groupKeyOf(a, groupIndex)) - order.get(groupKeyOf(b, groupIndex)))
            }
        }
        return list
    }

    $: rows = grid ? computeRows(grid, query, filters, sortKey, sortDirection) : []
    $: loadedCount = grid ? grid.lines.filter((line) => line && line.visible !== false).length : 0
    $: activeFilterCount = Object.values(filters).filter((values) => values?.length > 0).length + (query ? 1 : 0)
    $: groupIndex = grid ? grid.cols.findIndex((col) => col.isGroup) : -1

    function groupLabelAt(list: GridLine[], position: number, index: number): string {
        if (index < 0) return null
        if (position > 0 && groupKeyOf(list[position - 1], index) === groupKeyOf(list[position], index)) return null
        return formatContent(grid.cols[index].type, list[position].slots[index]?.content, grid.defaultEmptyValue)
    }

    function alignOf(col: GridCol): 'left' | 'center' | 'right' {
        if (col.align === GridColAlignEnum.left) return 'left'
        if (col.align === GridColAlignEnum.center) return 'center'
        if (col.align === GridColAlignEnum.right) return 'right'
        if (col.type === GridColTypeEnum.checkbox) return 'center'
        if (col.type === GridColTypeEnum.button || isNumericType(col.type)) return 'right'
        return 'left'
    }

    // ###################################################################### cabeçalho da tabela fixo ao rolar
    // o cabeçalho (th) é position: sticky. duas coisas precisam de medição, por isso a action:
    // - rolagem horizontal: só é ligada quando a tabela não cabe no corpo. overflow no corpo prende o sticky, então
    //   com tabela larga a rolagem lateral tem prioridade e o cabeçalho deixa de grudar (é o comportamento de antes)
    // - barra Zact sticky da tela (gruda em top: 8px, z-index 1): o cabeçalho gruda logo abaixo dela, não por baixo
    let bodyScrollable = false
    let stickyTop = 0

    function scrollParentOf(element: HTMLElement): HTMLElement {
        for (let parent = element?.parentElement; parent; parent = parent.parentElement) {
            if (/(auto|scroll)/.test(getComputedStyle(parent).overflowY)) return parent
        }
        return null
    }

    function stickyHead(body: HTMLElement) {
        const root = body.closest('.zgrid2') as HTMLElement
        const table = body.querySelector('table')
        const scroller = scrollParentOf(root)
        // só as barras sticky que ficam acima do grid na tela
        const bars = scroller
            ? Array.from(scroller.querySelectorAll<HTMLElement>('.zact.is-sticky')).filter(
                  (bar) => bar.compareDocumentPosition(root) & Node.DOCUMENT_POSITION_FOLLOWING,
              )
            : []
        const update = () => {
            bodyScrollable = !!table && table.offsetWidth > body.clientWidth + 1
            stickyTop = bars.reduce((top, bar) => Math.max(top, (parseFloat(getComputedStyle(bar).top) || 0) + bar.offsetHeight), 0)
        }
        const observer = new ResizeObserver(update)
        observer.observe(body)
        if (table) observer.observe(table)
        for (const bar of bars) observer.observe(bar)
        update()
        return {
            destroy() {
                observer.disconnect()
            },
        }
    }

    function widthOf(col: GridCol): string {
        return col.size ? `width:${(100 / 12) * col.size}%;` : ''
    }

    // ###################################################################### linha marcada (clique na linha)
    // mesma ideia do Zgrid: a linha clicada fica destacada, pra achar de novo a linha depois de "Abrir" e fechar.
    // clicar num botão/campo da linha sempre marca; clicar no resto da linha marca ou desmarca.
    let selectedLine: GridLine = null
    // conteúdo de cada coluna da linha marcada, pra reencontrá-la quando a tela manda um grid com linhas recriadas
    let selectedParts: string[] = null
    let selectedIndex = -1

    function partsOf(source: GridClass, line: GridLine): string[] {
        return source.cols.map((col, index) => (isInteractiveType(col.type) ? '' : fingerprintOf(line?.slots[index]?.content ?? null)))
    }

    function selectLine(line: GridLine, source: GridClass = grid) {
        selectedLine = line
        selectedIndex = source.lines.indexOf(line)
        selectedParts = partsOf(source, line)
    }

    function clearSelectedLine() {
        selectedLine = null
        selectedParts = null
        selectedIndex = -1
    }

    function onLineClick(event: MouseEvent, line: GridLine) {
        if (shared) return
        const target = event.target as HTMLElement
        // checkbox da seleção em massa é outro conceito: não mexe na linha marcada
        if (target?.closest('.z2check')) return
        const fromControl = !!target?.closest('button, a, input, select, textarea, label')
        if (line === selectedLine && !fromControl) clearSelectedLine()
        else selectLine(line)
    }

    // grid com dados novos: se a linha marcada (o mesmo objeto) continua lá, segue marcada. se as linhas foram
    // recriadas, marca a que tiver mais colunas iguais às da marcada, desde que mais da metade bata — é a mesma linha
    // com algum dado atualizado (ex.: status depois de editar). empate fica com a mais próxima da posição antiga.
    // colunas vazias não contam como evidência.
    function remapSelectedLine(source: GridClass) {
        if (!selectedLine) return
        if (source.lines.includes(selectedLine)) {
            selectLine(selectedLine, source)
            return
        }
        const comparable = (selectedParts || []).map((part, index) => (part && part !== '~' ? index : -1)).filter((index) => index >= 0)
        if (!selectedParts || selectedParts.length !== source.cols.length || comparable.length === 0) {
            clearSelectedLine()
            return
        }
        let best: GridLine = null
        let bestScore = 0
        let bestDistance = Infinity
        source.lines.forEach((line, index) => {
            if (!line) return
            let score = 0
            for (const column of comparable) {
                if (isInteractiveType(source.cols[column].type)) continue
                if (fingerprintOf(line.slots[column]?.content ?? null) === selectedParts[column]) score++
            }
            const distance = Math.abs(index - selectedIndex)
            if (score > bestScore || (score > 0 && score === bestScore && distance < bestDistance)) {
                best = line
                bestScore = score
                bestDistance = distance
            }
        })
        if (best && bestScore * 2 > comparable.length) selectLine(best, source)
        else clearSelectedLine()
    }

    // ###################################################################### filtro por coluna (popup)
    let filterColumn: ColumnView = null
    let filterAnchor: HTMLElement = null
    let filterOptions: FilterOption[] = []
    let filterPending: string[] = []
    let filterSearch = ''

    function openFilter(event: MouseEvent, column: ColumnView) {
        const options = new Map<string, FilterOption>()
        for (const line of grid.lines) {
            if (!line || line.visible === false) continue
            const slot = line.slots[column.index]
            for (const key of Array.from(new Set(filterKeysOf(column.col, slot)))) {
                const option = options.get(key)
                if (option) {
                    option.count++
                    continue
                }
                // datas e números em ordem cronológica/numérica, não alfabética do texto exibido
                const order =
                    key === '' ? null : column.col.type === GridColTypeEnum.badge || Array.isArray(slot?.content) ? key : sortValueOf(column.col, slot)
                options.set(key, { key, label: key === '' ? 'Sem valor' : key, count: 1, order })
            }
        }
        filterOptions = Array.from(options.values()).sort((a, b) => compareSortValues(a.order, b.order))
        filterPending = [...(filters[column.key] || [])]
        filterSearch = ''
        filterAnchor = event.currentTarget as HTMLElement
        filterColumn = column
    }

    $: shownFilterOptions = filterOptions.filter((option) => normalizeText(option.label).includes(normalizeText(filterSearch)))

    function togglePending(key: string) {
        filterPending = filterPending.includes(key) ? filterPending.filter((value) => value !== key) : [...filterPending, key]
    }

    function applyFilter() {
        const keys = filterPending.filter((key) => filterOptions.some((option) => option.key === key))
        const next = { ...filters }
        // nada marcado ou tudo marcado dá no mesmo que sem filtro
        if (keys.length === 0 || keys.length === filterOptions.length) delete next[filterColumn.key]
        else next[filterColumn.key] = keys
        filters = next
        closePopups()
    }

    function resetFilter() {
        const next = { ...filters }
        delete next[filterColumn.key]
        filters = next
        closePopups()
    }

    // ###################################################################### seleção em massa (col.type = checkbox)
    // a barra verde e as ações consideram só as linhas visíveis (busca/filtro em vigor), mesmo critério do Zgrid
    $: checkboxColumn = columns.find((view) => view.col.type === GridColTypeEnum.checkbox) || null
    $: checkedRows = checkboxColumn ? rows.filter((line) => line.checked) : []
    $: allChecked = rows.length > 0 && checkedRows.length === rows.length
    $: someChecked = checkedRows.length > 0 && !allChecked

    function toggleAll() {
        const next = !allChecked
        for (const line of rows) line.checked = next
        grid = grid
    }

    function toggleLine(line: GridLine) {
        line.checked = !line.checked
        grid = grid
    }

    function clearChecked() {
        for (const line of grid.lines) if (line) line.checked = false
        grid = grid
    }

    function runBulkAction(button: GridButton) {
        if (button.disabled) return
        const values = checkedRows.map((line) => line.slots[checkboxColumn.index]?.content)
        button.callback?.(values)
    }

    function indeterminate(node: HTMLInputElement, value: boolean) {
        node.indeterminate = value
        return {
            update(next: boolean) {
                node.indeterminate = next
            },
        }
    }

    function autofocus(node: HTMLInputElement) {
        node.focus({ preventScroll: true })
    }

    // ###################################################################### totais
    function totalOf(index: number, list: GridLine[]): number {
        let total = 0
        for (const line of list) {
            if (line.removed) continue
            const value = toNumber(line.slots[index]?.content)
            if (value !== null) total += value
        }
        return total
    }

    function totalDisplay(col: GridCol, index: number, list: GridLine[]): string {
        const type = isNumericType(col.type) && col.type !== GridColTypeEnum.percent ? col.type : GridColTypeEnum.float_2
        return formatContent(type, totalOf(index, list), '0')
    }

    function boxDisplay(box: GridBox): string {
        const decimals = box.decimals || 0
        return Number(box.value ?? 0).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
    }

    $: firstTotalPosition = columns.findIndex((view) => view.col.showTotal)
    $: haveTotals = !isMobile && firstTotalPosition >= 0

    // colunas que o papel esconde (ver @media print, no fim deste arquivo): seleção e botões
    function hiddenOnPrint(col: GridCol): boolean {
        return col.type === GridColTypeEnum.checkbox || col.type === GridColTypeEnum.button
    }

    // O rótulo da linha de total cobre tudo o que vem antes do primeiro total. Um colspan único sobre
    // essas colunas quebra na impressão: as de seleção e de botões somem lá, mas continuam contadas no
    // colspan, e a linha inteira sai uma coluna à frente — o último total chega a cair fora da tabela.
    // Por isso o rótulo vira blocos: cada coluna que some no papel ganha uma célula própria (some
    // junto, como nas demais linhas) e o colspan fica só sobre as colunas que existem nos dois meios.
    // O texto vai no primeiro bloco de rótulo, mantendo o "Total" o mais à esquerda possível.
    type TotalLabelBlock = { kind: 'hidden'; col: GridCol } | { kind: 'label'; span: number; withText: boolean }
    function totalLabelBlocksOf(views: ColumnView[], until: number): TotalLabelBlock[] {
        const blocks: TotalLabelBlock[] = []
        let textUsed = false
        for (const view of views.slice(0, until)) {
            if (hiddenOnPrint(view.col)) {
                blocks.push({ kind: 'hidden', col: view.col })
                continue
            }
            const last = blocks[blocks.length - 1]
            if (last && last.kind === 'label') {
                last.span++
                continue
            }
            blocks.push({ kind: 'label', span: 1, withText: !textUsed })
            textUsed = true
        }
        return blocks
    }
    $: totalLabelBlocks = haveTotals && firstTotalPosition > 0 ? totalLabelBlocksOf(columns, firstTotalPosition) : []
    $: totalBoxColumns = viewsOf(grid).filter((view) => view.col.showTotalBox)
    $: cardFields = columns.filter((view) => view.col.type !== GridColTypeEnum.button)
    $: cardButtons = columns.filter((view) => view.col.type === GridColTypeEnum.button)

    // ###################################################################### acts
    // act com "active" definido vai pro grupo segmentado; sem ele (caso de todo act do Zgrid), vira botão comum
    $: segmentActs = (grid?.acts || []).filter((act) => act && act.active !== null && act.active !== undefined)
    $: plainActs = (grid?.acts || []).filter((act) => act && (act.active === null || act.active === undefined))

    // usado pelo grupo segmentado (no desktop) e pelo seletor que o substitui no celular
    function applySegment(act: GridButton, anchor: HTMLElement) {
        if (act.disabled) return
        // type "date" e "select" só ficam ativos depois da escolha (ver onDatePicked e pickSelectOption)
        if (act.type === 'date') return openDateAct(act, anchor, 'left')
        if (act.type === 'select') return openSelectAct(act, anchor, 'left')
        for (const item of segmentActs) item.active = item === act
        grid = grid
        act.callback?.()
    }

    function clickSegment(act: GridButton, event: MouseEvent) {
        applySegment(act, event.currentTarget as HTMLElement)
    }

    function clickPlainAct(act: GridButton, event: MouseEvent) {
        if (act.disabled) return
        const anchor = event.currentTarget as HTMLElement
        if (act.type === 'date') openDateAct(act, anchor, 'right')
        else if (act.type === 'select') openSelectAct(act, anchor, 'right')
        else act.callback?.(event)
    }

    // no celular o grupo segmentado inteiro vira um seletor: o botão mostra a opção ativa e o popup lista as opções.
    // isso é por dentro do grid — a tela configura os acts do mesmo jeito nos dois casos
    let segmentMenuAnchor: HTMLElement = null

    function openSegmentMenu(event: MouseEvent) {
        segmentMenuAnchor = event.currentTarget as HTMLElement
    }

    function pickSegmentFromMenu(act: GridButton) {
        const anchor = segmentMenuAnchor
        closePopups()
        // um item type "date"/"select" abre o popup dele em seguida, ancorado no próprio botão do seletor
        applySegment(act, anchor)
    }

    // act type "date": popup com o date-select (skin "grid"). escolhido o período, guarda em dateFrom/dateTo, marca como
    // ativo (se for do grupo segmentado) e chama o callback com { from, to }
    let dateAct: GridButton = null
    let dateAnchor: HTMLElement = null
    let dateAlign: 'left' | 'right' = 'left'

    function openDateAct(act: GridButton, anchor: HTMLElement, align: 'left' | 'right') {
        dateAnchor = anchor
        dateAlign = align
        dateAct = act
    }

    function onDatePicked(event: CustomEvent<{ from: Date; to: Date }>) {
        const act = dateAct
        closePopups()
        if (!act) return
        const { from, to } = event.detail
        act.dateFrom = from
        act.dateTo = to
        if (act.active !== null && act.active !== undefined) {
            for (const item of segmentActs) item.active = item === act
        }
        grid = grid
        act.callback?.({ from, to })
    }

    // act type "select": popup com as options. escolhida uma, guarda em value, marca como ativo (se for do grupo
    // segmentado) e chama o callback com o value
    let selectAct: GridButton = null
    let selectAnchor: HTMLElement = null
    let selectAlign: 'left' | 'right' = 'left'

    function openSelectAct(act: GridButton, anchor: HTMLElement, align: 'left' | 'right') {
        selectAnchor = anchor
        selectAlign = align
        selectAct = act
    }

    function pickSelectOption(option: { value: any; label: string }) {
        const act = selectAct
        closePopups()
        if (!act) return
        act.value = option.value
        if (act.active !== null && act.active !== undefined) {
            for (const item of segmentActs) item.active = item === act
        }
        grid = grid
        act.callback?.(option.value)
    }

    // type "date"/"select" com escolha feita mostram o intervalo ou a opção no lugar do texto (no grupo segmentado,
    // só quando ativo). o texto do act vira o título do popup e a dica do botão
    function actLabelOf(act: GridButton): string {
        if (act.type === 'date' && act.dateFrom && act.dateTo && act.active !== false) return periodLabel(act.dateFrom, act.dateTo)
        if (act.type === 'select' && act.active !== false) {
            const option = (act.options || []).find((item) => item.value === act.value)
            if (option) return option.label
        }
        return act.label
    }

    // act type "date" fora do grupo segmentado (que funciona como um radio, e lá o ícone só polui) ganha um ícone de
    // calendário, pra deixar claro que o botão abre um seletor de período
    function actIconOf(act: GridButton) {
        if (act.icon) return act.icon
        const isSegment = act.active !== null && act.active !== undefined
        if (act.type === 'date' && !isSegment) return faCalendarAlt
        return null
    }

    function periodLabel(from: Date, to: Date): string {
        const start = dayjs(from)
        const end = dayjs(to)
        const currentYear = dayjs().year()
        const format = start.year() === currentYear && end.year() === currentYear ? 'DD/MM' : 'DD/MM/YY'
        return start.isSame(end, 'day') ? start.format(format) : `${start.format(format)} – ${end.format(format)}`
    }

    function actTone(color: GridButton['color']): string {
        if (color === 'green' || color === 'primary') return 'z2btn--solid'
        if (color === 'red') return 'z2btn--danger'
        if (color === 'yellow') return 'z2btn--warning'
        return ''
    }

    $: showSearch = !!grid && grid.showActs !== false
    // "Ações" e "Colunas" não aparecem no celular: imprimir/compartilhar/exportar e a escolha de colunas são coisas de
    // tela grande, e lá a barra vira uma pilha de botões ocupando o topo. a busca continua
    $: showActionsButton = !!grid && grid.showActs !== false && !isMobile
    $: showColumnsButton = !!grid && grid.showColumns !== false && toggleableColumns.length > 1 && !isMobile
    // acts só aparecem com o cabeçalho ligado (como no Zgrid); os controles do grid aparecem mesmo sem ele
    $: hasActs = !shared && !!grid && grid.showHeaders !== false && (segmentActs.length > 0 || plainActs.length > 0)
    $: hasControls = !shared && (showSearch || showActionsButton || showColumnsButton)
    $: visibleSegmentActs = segmentActs.filter((act) => !isMobile || act.mobile !== GridItemShow.hide)
    $: activeSegmentAct = visibleSegmentActs.find((act) => act.active) || null
    $: segmentButtonLabel = activeSegmentAct ? actLabelOf(activeSegmentAct) : 'Filtro'

    // ###################################################################### popups
    let actionsAnchor: HTMLElement = null
    let columnsAnchor: HTMLElement = null
    let rowMenu: { anchor: HTMLElement; buttons: GridButton[] } = null

    function closePopups() {
        actionsAnchor = null
        columnsAnchor = null
        filterColumn = null
        filterAnchor = null
        rowMenu = null
        dateAct = null
        dateAnchor = null
        selectAct = null
        selectAnchor = null
        segmentMenuAnchor = null
    }

    function runRowButton(button: GridButton, event: MouseEvent) {
        if (button.disabled) return
        rowMenu = null
        button.callback?.(event)
    }

    // ###################################################################### imprimir, compartilhar, exportar
    // os três usam só o que está na tela: colunas visíveis (sem as interativas) e linhas após busca/filtro/ordem
    function snapshotColumns(): ColumnView[] {
        return orderedViews(grid, columnPrefs).filter(
            (view) => view.col.isGroup || (!isInteractiveType(view.col.type) && columns.some((item) => item.index === view.index)),
        )
    }

    // cópia "estática" do grid: sem callbacks, sem checkbox (o content dela costuma ser a chave do registro, que
    // não deve ir no payload do link público) e com os filtros já aplicados
    function snapshotGrid(): GridClass {
        const cols = snapshotColumns()
        return new GridClass({
            title: grid.title,
            subtitle: grid.subtitle,
            defaultEmptyValue: grid.defaultEmptyValue,
            totalLabel: grid.totalLabel,
            fontSize: grid.fontSize,
            minWidth: grid.minWidth,
            showHeaders: grid.showHeaders,
            showLabels: grid.showLabels,
            showLines: grid.showLines,
            showActs: false,
            showFilter: false,
            showColumns: false,
            box: (grid.box || []).map((box) => ({ ...box })),
            cols: cols.map(
                (view) =>
                    new GridCol({ ...view.col, filter: [], bulkActions: [], visibility: GridColVisibilityEnum.visible, sortable: false, filterable: false }),
            ),
            lines: rows.map(
                (line) =>
                    new GridLine({
                        class: line.class,
                        removed: line.removed,
                        selected: line.selected,
                        slots: cols.map((view) => {
                            const slot = line.slots[view.index]
                            return new GridSlot({
                                content: slot?.content ?? null,
                                subtitle: slot?.subtitle || '',
                                badge: slot?.badge || null,
                                class: slot?.class || '',
                            })
                        }),
                    }),
            ),
        })
    }

    async function printGrid() {
        closePopups()
        const me = (await import('./zgrid2.svelte')).default
        window.printer({ component: [me], props: [{ grid: snapshotGrid(), shared: true }] })
    }

    let shareToken: string = null
    async function shareGrid() {
        closePopups()
        try {
            const dto: Partial<SharePageEntity> = {
                data: [{ type: 'zgrid2', data: { grid: snapshotGrid(), shared: true } }],
            }
            const result = await window.api.tools.sharePages.create(dto)
            shareToken = result.sharePageId + '_' + result.token
        } catch (error) {
            window.error(error)
        }
    }

    function exportCsv() {
        closePopups()
        const cols = snapshotColumns()
        const escape = (value: string) => (/[;"\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value)
        const csv = [cols.map((view) => escape(String(view.col.content || ''))).join(';')]
        for (const line of rows) {
            if (line.selected === false) continue
            csv.push(cols.map((view) => escape(csvValueOf(view.col, line.slots[view.index]))).join(';'))
        }
        const filename = ([grid.title, grid.subtitle].filter(Boolean).join('_') || 'tabela').replace(/[\\/:*?"<>|]/g, '-') + '.csv'
        const link = document.createElement('a')
        link.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent('﻿' + csv.join('\n')))
        link.setAttribute('download', filename)
        link.click()
    }
</script>

{#if shareToken}
    <Popover on:close={() => (shareToken = null)} width="780px" title="Link de compartilhamento">
        <SharePageLink token={shareToken}></SharePageLink>
    </Popover>
{/if}

{#if grid}
    <!-- a casca (cartão: fundo, borda, raio, recorte) é o Zblock, o mesmo do Zframe e do
         ZsheetSection. O cabeçalho logo abaixo continua sendo da tabela: nele moram busca,
         colunas, filtros e ações de seleção, que não são um rótulo de bloco. -->
    <Zblock
        densidade="barra"
        flush={true}
        class="zgrid2 {className} {isMobile ? 'is-mobile' : ''} {shared ? 'is-paper' : ''}"
        style={(grid.fontSize ? `font-size:${grid.fontSize};` : '') + `--z2-sticky-top:${stickyTop}px;`}
    >
        <!-- ############################################################ cabeçalho: título, acts e controles do grid -->
        {#if grid.showHeaders !== false || hasControls}
            <div class="z2head" class:has-acts={hasActs} class:is-bare={grid.showHeaders === false}>
                {#if grid.showHeaders !== false}
                    <div class="z2head_info">
                        {#if grid.title}
                            <div class="z2title">
                                <span>{grid.title}</span>
                                <span class="z2count">{loadedCount}</span>
                            </div>
                        {/if}
                        {#if grid.subtitle}
                            <div class="z2subtitle">{grid.subtitle}</div>
                        {/if}
                    </div>
                {/if}
                {#if hasActs}
                    <div class="z2head_acts">
                        {#if segmentActs.length > 0}
                            {#if isMobile}
                                <button type="button" class="z2btn" class:is-open={!!segmentMenuAnchor} on:click={openSegmentMenu}>
                                    <span class="z2label">{segmentButtonLabel}</span>
                                    <span class="z2caret"><Fa icon={faChevronDown} /></span>
                                </button>
                            {:else}
                                <div class="z2segments" role="tablist">
                                    {#each segmentActs as act}
                                        <button
                                            type="button"
                                            role="tab"
                                            aria-selected={!!act.active}
                                            class="z2segment {act.class || ''}"
                                            class:is-active={act.active}
                                            disabled={act.disabled}
                                            title={act.title || (act.type === 'date' || act.type === 'select' ? act.label : '')}
                                            on:click={(event) => clickSegment(act, event)}
                                        >
                                            {#if act.icon}<Fa icon={act.icon} />{/if}
                                            {#if act.label}<span class="z2label">{actLabelOf(act)}</span>{/if}
                                        </button>
                                    {/each}
                                </div>
                            {/if}
                        {/if}
                        {#each plainActs as act}
                            {#if !isMobile || act.mobile !== GridItemShow.hide}
                                <button
                                    type="button"
                                    class="z2btn {actTone(act.color)} {act.class || ''}"
                                    disabled={act.disabled}
                                    title={act.title || ''}
                                    class:is-open={dateAct === act || selectAct === act}
                                    on:click={(event) => clickPlainAct(act, event)}
                                >
                                    {#if actIconOf(act)}<Fa icon={actIconOf(act)} />{/if}
                                    {#if act.label && (!isMobile || act.mobileShowLabel !== false)}<span class="z2label">{actLabelOf(act)}</span>{/if}
                                </button>
                            {/if}
                        {/each}
                    </div>
                {/if}
                <!-- controles do grid (busca, Ações, Colunas): sem acts ficam na linha do título; com acts, ou no
                     celular, descem pra uma linha própria com divisória — quem decide é o CSS de .z2controls -->
                {#if hasControls}
                    <div class="z2controls">
                        {#if showSearch}
                            <div class="z2search">
                                <input
                                    class="z2input"
                                    type="text"
                                    placeholder={grid.searchPlaceholder || 'Buscar nos dados carregados'}
                                    bind:value={searchText}
                                    on:input={onSearchInput}
                                    on:keydown={(event) => event.key === 'Escape' && clearSearch()}
                                />
                                {#if searchText}
                                    <button type="button" class="z2search_clear" title="Limpar busca" aria-label="Limpar busca" on:click={clearSearch}>
                                        <Fa icon={faTimes} />
                                    </button>
                                {/if}
                            </div>
                        {/if}
                        {#if showActionsButton || showColumnsButton}
                            <div class="z2controls_buttons">
                                {#if showActionsButton}
                                    <button
                                        type="button"
                                        class="z2btn"
                                        class:is-open={!!actionsAnchor}
                                        on:click={(event) => (actionsAnchor = event.currentTarget)}
                                    >
                                        <span class="z2label">Ações</span>
                                        <span class="z2caret"><Fa icon={faChevronDown} /></span>
                                    </button>
                                {/if}
                                {#if showColumnsButton}
                                    <button type="button" class="z2btn" class:is-open={!!columnsAnchor} on:click={openColumns}>
                                        <span class="z2label">Colunas</span>
                                    </button>
                                {/if}
                            </div>
                        {/if}
                    </div>
                {/if}
            </div>
        {/if}

        <!-- ############################################################ barra de seleção em massa -->
        {#if checkboxColumn && checkedRows.length > 0}
            <div class="z2selection" transition:slide={{ duration: 150 }}>
                <span class="z2selection_text">{plural(checkedRows.length, 'item selecionado', 'itens selecionados')}</span>
                <div class="z2spacer"></div>
                {#each checkboxColumn.col.bulkActions || [] as action}
                    <button
                        type="button"
                        class="z2btn z2btn--solid {action.class || ''}"
                        disabled={action.disabled}
                        title={action.title || ''}
                        on:click={() => runBulkAction(action)}
                    >
                        {#if action.icon}<Fa icon={action.icon} />{/if}
                        {#if action.label}<span class="z2label">{action.label}</span>{/if}
                    </button>
                {/each}
                <button type="button" class="z2btn z2btn--ghost" on:click={clearChecked}><span class="z2label">Limpar</span></button>
            </div>
        {/if}

        <!-- ############################################################ caixas de resumo -->
        {#if totalBoxColumns.length > 0 || grid.box?.length > 0}
            <div class="z2boxes">
                {#each totalBoxColumns as view (view.key)}
                    <div class="z2box">
                        <div class="z2box_label">{view.col.content}</div>
                        <div class="z2box_value">{totalDisplay(view.col, view.index, rows)}</div>
                    </div>
                {/each}
                {#each grid.box || [] as box}
                    <div class="z2box {box.class || ''}">
                        <div class="z2box_label">{box.label}</div>
                        <div class="z2box_value">{boxDisplay(box)}</div>
                    </div>
                {/each}
            </div>
        {/if}

        <!-- ############################################################ dados -->
        {#if isMobile}
            <div class="z2cards">
                {#each rows as line, lineIndex}
                    {@const groupLabel = groupLabelAt(rows, lineIndex, groupIndex)}
                    {#if groupLabel !== null}
                        <div class="z2group_card">{groupLabel}</div>
                    {/if}
                    <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
                    <div
                        class="z2card {line.class || ''}"
                        class:is-removed={line.removed}
                        class:is-selected={line === selectedLine}
                        on:click={(event) => onLineClick(event, line)}
                    >
                        {#each cardFields as view (view.key)}
                            <div class="z2card_field {line.slots[view.index]?.class || ''}">
                                {#if view.col.content}<div class="z2card_label">{view.col.content}</div>{/if}
                                <div class="z2card_value">
                                    <Zgrid2Cell col={view.col} slot={line.slots[view.index]} {isMobile} emptyValue={grid.defaultEmptyValue} />
                                </div>
                            </div>
                        {/each}
                        {#each cardButtons as view (view.key)}
                            <div class="z2card_buttons">
                                <Zgrid2Cell
                                    col={view.col}
                                    slot={line.slots[view.index]}
                                    {isMobile}
                                    emptyValue={grid.defaultEmptyValue}
                                    on:menu={(event) => (rowMenu = event.detail)}
                                />
                            </div>
                        {/each}
                    </div>
                {:else}
                    <div class="z2empty_card">
                        {#if loadedCount === 0}
                            Nenhuma informação para exibir
                        {:else}
                            Nenhum registro encontrado para a busca.
                            <button type="button" class="z2link" on:click={clearSearchAndFilters}>Limpar busca</button>
                        {/if}
                    </div>
                {/each}
            </div>
        {:else}
            <div class="z2body" class:is-scrollable={bodyScrollable} use:stickyHead>
                <table class="z2table" style={grid.minWidth ? `min-width:${grid.minWidth};` : ''}>
                    {#if grid.showLabels !== false}
                        <thead>
                            <tr>
                                {#each columns as view (view.key)}
                                    {@const align = alignOf(view.col)}
                                    <th
                                        class="z2th"
                                        class:z2th--check={view.col.type === GridColTypeEnum.checkbox}
                                        class:z2th--buttons={view.col.type === GridColTypeEnum.button}
                                        class:is-right={align === 'right'}
                                        class:is-center={align === 'center'}
                                        style={widthOf(view.col)}
                                    >
                                        {#if view.col.type === GridColTypeEnum.checkbox}
                                            <input
                                                class="z2check"
                                                type="checkbox"
                                                aria-label="Selecionar todos"
                                                checked={allChecked}
                                                use:indeterminate={someChecked}
                                                on:change={toggleAll}
                                            />
                                        {:else if view.col.content}
                                            <span class="z2th_inner">
                                                {#if !shared && grid.showFilter !== false && isSortable(view.col)}
                                                    <button
                                                        type="button"
                                                        class="z2th_sort"
                                                        class:is-sorted={sortKey === view.key}
                                                        on:click={() => toggleSort(view.key)}
                                                    >
                                                        <span>{view.col.content}</span>
                                                        <span class="z2sort_icon">
                                                            {#if sortKey !== view.key}
                                                                <Fa icon={faSort} />
                                                            {:else if sortDirection === 'asc'}
                                                                <Fa icon={faSortUp} />
                                                            {:else}
                                                                <Fa icon={faSortDown} />
                                                            {/if}
                                                        </span>
                                                    </button>
                                                {:else}
                                                    <span>{view.col.content}</span>
                                                {/if}
                                                {#if !shared && grid.showFilter !== false && isFilterable(view.col)}
                                                    <button
                                                        type="button"
                                                        class="z2th_filter"
                                                        class:is-active={filters[view.key]?.length > 0}
                                                        class:is-open={filterColumn?.key === view.key}
                                                        title="Filtrar"
                                                        aria-label="Filtrar {view.col.content}"
                                                        on:click={(event) => openFilter(event, view)}
                                                    >
                                                        <Fa icon={faFilter} />
                                                    </button>
                                                {/if}
                                            </span>
                                        {/if}
                                    </th>
                                {/each}
                            </tr>
                        </thead>
                    {/if}
                    <tbody>
                        {#each rows as line, lineIndex}
                            {@const groupLabel = groupLabelAt(rows, lineIndex, groupIndex)}
                            {#if groupLabel !== null}
                                <tr class="z2group"><td colspan={columns.length}>{groupLabel}</td></tr>
                            {/if}
                            <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-noninteractive-element-interactions -->
                            <tr
                                class="z2tr {line.class || ''}"
                                class:is-checked={line.checked && !!checkboxColumn}
                                class:is-selected={line === selectedLine}
                                class:is-removed={line.removed}
                                class:no-lines={!grid.showLines}
                                on:click={(event) => onLineClick(event, line)}
                            >
                                {#each columns as view (view.key)}
                                    {@const align = alignOf(view.col)}
                                    <td
                                        class="z2td {view.col.class || ''} {line.slots[view.index]?.class || ''}"
                                        class:z2td--check={view.col.type === GridColTypeEnum.checkbox}
                                        class:z2td--buttons={view.col.type === GridColTypeEnum.button}
                                        class:is-right={align === 'right'}
                                        class:is-center={align === 'center'}
                                        style={widthOf(view.col)}
                                    >
                                        {#if view.col.type === GridColTypeEnum.checkbox}
                                            <input
                                                class="z2check"
                                                type="checkbox"
                                                aria-label="Selecionar linha"
                                                checked={line.checked}
                                                on:change={() => toggleLine(line)}
                                            />
                                        {:else}
                                            <Zgrid2Cell
                                                col={view.col}
                                                slot={line.slots[view.index]}
                                                {isMobile}
                                                emptyValue={grid.defaultEmptyValue}
                                                on:menu={(event) => (rowMenu = event.detail)}
                                            />
                                        {/if}
                                    </td>
                                {/each}
                            </tr>
                        {:else}
                            <tr class="z2empty">
                                <td colspan={Math.max(columns.length, 1)}>
                                    {#if loadedCount === 0}
                                        Nenhuma informação para exibir
                                    {:else}
                                        Nenhum registro encontrado para a busca ou os filtros aplicados.
                                        <button type="button" class="z2link" on:click={clearSearchAndFilters}>Limpar busca e filtros</button>
                                    {/if}
                                </td>
                            </tr>
                        {/each}
                    </tbody>
                    {#if haveTotals && rows.length > 0}
                        <tfoot>
                            <tr class="z2total">
                                {#each totalLabelBlocks as block}
                                    {#if block.kind === 'hidden'}
                                        <td
                                            class:z2td--check={block.col.type === GridColTypeEnum.checkbox}
                                            class:z2td--buttons={block.col.type === GridColTypeEnum.button}
                                        ></td>
                                    {:else}
                                        <td colspan={block.span} class="z2total_label">{block.withText ? grid.totalLabel || 'Total' : ''}</td>
                                    {/if}
                                {/each}
                                {#each columns.slice(firstTotalPosition) as view (view.key)}
                                    {@const align = alignOf(view.col)}
                                    <td
                                        class={view.col.class || ''}
                                        class:z2td--check={view.col.type === GridColTypeEnum.checkbox}
                                        class:z2td--buttons={view.col.type === GridColTypeEnum.button}
                                        class:is-right={align === 'right'}
                                        class:is-center={align === 'center'}
                                    >
                                        {#if view.col.showTotal}
                                            <span class="z2total_value">{totalDisplay(view.col, view.index, rows)}</span>
                                        {/if}
                                    </td>
                                {/each}
                            </tr>
                        </tfoot>
                    {/if}
                </table>
            </div>
        {/if}

        <!-- ############################################################ rodapé -->
        {#if !shared && loadedCount > 0}
            <div class="z2foot">
                <span>
                    Mostrando
                    {rows.length === loadedCount
                        ? plural(rows.length, 'registro', 'registros')
                        : `${rows.length} de ${plural(loadedCount, 'registro', 'registros')}`}
                </span>
                <div class="z2spacer"></div>
                <!-- paginação (Anterior / Próxima) entra aqui quando o carregamento paginado existir -->
            </div>
        {/if}

        <!-- ############################################################ popups -->
        {#if actionsAnchor}
            <Zgrid2Popup anchor={actionsAnchor} align="right" width={300} on:close={closePopups}>
                {#if grid.title}<div class="z2menu_title">{grid.title}</div>{/if}
                <div class="z2menu_list">
                    <button type="button" class="z2menu_item" on:click={printGrid}>
                        <span class="z2menu_icon"><Fa icon={faPrint} /></span>
                        <span class="z2menu_text">
                            <span class="z2menu_label">Imprimir tabela</span>
                            <span class="z2menu_hint">Só as colunas e linhas visíveis</span>
                        </span>
                    </button>
                    <button type="button" class="z2menu_item" on:click={shareGrid}>
                        <span class="z2menu_icon"><Fa icon={faShareAlt} /></span>
                        <span class="z2menu_text">
                            <span class="z2menu_label">Compartilhar link</span>
                            <span class="z2menu_hint">Link com os filtros aplicados</span>
                        </span>
                    </button>
                    <button type="button" class="z2menu_item" on:click={exportCsv}>
                        <span class="z2menu_icon"><Fa icon={faFileExport} /></span>
                        <span class="z2menu_text">
                            <span class="z2menu_label">Exportar CSV</span>
                            <span class="z2menu_hint">
                                {plural(rows.length, 'linha', 'linhas')} · {plural(snapshotColumns().length, 'coluna', 'colunas')}
                            </span>
                        </span>
                    </button>
                </div>
                <div class="z2menu_foot">
                    {plural(rows.length, 'linha', 'linhas')} ·
                    {activeFilterCount === 0 ? 'sem filtros ativos' : plural(activeFilterCount, 'filtro ativo', 'filtros ativos')}
                </div>
            </Zgrid2Popup>
        {/if}

        {#if columnsAnchor}
            <Zgrid2Popup anchor={columnsAnchor} align="right" width={290} on:close={closePopups}>
                <div class="z2colhead">
                    <span class="z2colhead_title">Colunas visíveis</span>
                    <button type="button" class="z2link" disabled={draftIsDefault} on:click={restoreColumnsDraft}>Restaurar</button>
                </div>
                <div class="z2collist" class:is-dragging={!!draggingKey} bind:this={columnsList}>
                    {#each columnsDraft as item (item.key)}
                        <div class="z2colitem" class:is-dragging={draggingKey === item.key} data-column-key={item.key} animate:flip={{ duration: 160 }}>
                            <button
                                type="button"
                                class="z2grip"
                                title="Arraste para reordenar"
                                aria-label="Mover a coluna {item.title}"
                                on:pointerdown={(event) => startColumnDrag(event, item.key)}
                                on:keydown={(event) => onGripKeydown(event, item.key)}
                            >
                                <Fa icon={faGripVertical} />
                            </button>
                            <label class="z2colitem_option" class:is-fixed={item.fixed}>
                                <input
                                    class="z2check"
                                    type="checkbox"
                                    checked={item.visible}
                                    disabled={item.fixed}
                                    on:change={() => toggleDraftColumn(item.key)}
                                />
                                <span class="z2option_label">{item.title}</span>
                            </label>
                            {#if item.fixed}<span class="z2colitem_fixed">fixa</span>{/if}
                        </div>
                    {/each}
                </div>
                <div class="z2filter_foot">
                    <span class="z2colfoot_count">{draftVisibleCount} de {plural(columnsDraft.length, 'coluna visível', 'colunas visíveis')}</span>
                    <button type="button" class="z2btn z2btn--solid" disabled={draftVisibleCount === 0} on:click={applyColumns}>
                        <span class="z2label">Aplicar</span>
                    </button>
                </div>
            </Zgrid2Popup>
        {/if}

        {#if filterColumn}
            <Zgrid2Popup anchor={filterAnchor} align="left" width={280} on:close={closePopups}>
                <div class="z2filter_head">
                    <input
                        class="z2input"
                        type="text"
                        placeholder="Buscar valor..."
                        bind:value={filterSearch}
                        use:autofocus
                        on:keydown={(event) => event.key === 'Enter' && applyFilter()}
                    />
                    <button type="button" class="z2icon_btn" title="Fechar" aria-label="Fechar" on:click={closePopups}>
                        <Fa icon={faTimes} />
                    </button>
                </div>
                <div class="z2menu_list">
                    {#each shownFilterOptions as option (option.key)}
                        <label class="z2option">
                            <input class="z2check" type="checkbox" checked={filterPending.includes(option.key)} on:change={() => togglePending(option.key)} />
                            <span class="z2option_label" title={option.label}>{option.label}</span>
                            <span class="z2option_count">{option.count}</span>
                        </label>
                    {:else}
                        <div class="z2menu_empty">Nenhum valor encontrado</div>
                    {/each}
                </div>
                <div class="z2filter_foot">
                    <button type="button" class="z2link z2link--muted" on:click={resetFilter}>Todos os valores</button>
                    <button type="button" class="z2btn z2btn--solid" on:click={applyFilter}><span class="z2label">Aplicar</span></button>
                </div>
            </Zgrid2Popup>
        {/if}

        {#if dateAct}
            <Zgrid2Popup anchor={dateAnchor} align={dateAlign} width={300} on:close={closePopups}>
                <DateSelect skin="grid" startFrom={dateAct.dateFrom} startTo={dateAct.dateTo} on:change={onDatePicked} />
            </Zgrid2Popup>
        {/if}

        {#if segmentMenuAnchor}
            <Zgrid2Popup anchor={segmentMenuAnchor} align="left" width={260} on:close={closePopups}>
                <div class="z2menu_list">
                    {#each visibleSegmentActs as act}
                        <button
                            type="button"
                            class="z2menu_item z2menu_item--compact {act.class || ''}"
                            class:is-selected={act.active}
                            disabled={act.disabled}
                            on:click={() => pickSegmentFromMenu(act)}
                        >
                            <span class="z2label">{actLabelOf(act)}</span>
                            {#if act.active}<span class="z2menu_item_check"><Fa icon={faCheck} /></span>{/if}
                        </button>
                    {/each}
                </div>
            </Zgrid2Popup>
        {/if}

        {#if selectAct}
            <Zgrid2Popup anchor={selectAnchor} align={selectAlign} width={280} on:close={closePopups}>
                {#if selectAct.label}<div class="z2menu_title">{selectAct.label}</div>{/if}
                <div class="z2menu_list">
                    {#each selectAct.options || [] as option}
                        <button
                            type="button"
                            class="z2menu_item z2menu_item--compact"
                            class:is-selected={option.value === selectAct.value}
                            on:click={() => pickSelectOption(option)}
                        >
                            <span class="z2label">{option.label}</span>
                            {#if option.value === selectAct.value}<span class="z2menu_item_check"><Fa icon={faCheck} /></span>{/if}
                        </button>
                    {/each}
                </div>
            </Zgrid2Popup>
        {/if}

        {#if rowMenu}
            <Zgrid2Popup anchor={rowMenu.anchor} align="right" width={220} on:close={closePopups}>
                <div class="z2menu_list">
                    {#each rowMenu.buttons as button}
                        <button
                            type="button"
                            class="z2menu_item z2menu_item--compact {button.class || ''}"
                            class:is-danger={button.color === 'red'}
                            disabled={button.disabled}
                            on:click={(event) => runRowButton(button, event)}
                        >
                            {#if button.icon}<span class="z2menu_item_icon"><Fa icon={button.icon} /></span>{/if}
                            <span class="z2label">{button.label || button.title}</span>
                        </button>
                    {/each}
                </div>
            </Zgrid2Popup>
        {/if}
    </Zblock>
{/if}

<style>
    /* ------------------------------------------------------------------ tema
       escuro é o padrão real do app (conta nova não recebe classe de tema nenhuma), então é o CSS base.
       claro: .theme_light no body. papel (compartilhado/impressão): sempre claro, independente do tema.
       :global porque a raiz é desenhada pelo Zblock (ver markup): sem isto o Svelte descarta a regra e TODAS
       as cores da tabela somem. Regras em que .zgrid2 é só ancestral continuam escopadas normalmente. */
    :global(.zgrid2) {
        /* TESTE: cinzas do Zgrid original (css_z.css). valor anterior do Zgrid2 comentado em cada linha */
        --z2-bg: #99999910; /* antes #1d1e20 */
        --z2-bg-soft: #99999920; /* antes #232427 */
        /* cabeçalho da tabela fixo ao rolar precisa de fundo opaco, senão as linhas aparecem através dele. é a cor
           que a faixa #99999920 resulta sobre o card e o fundo escuro da tela (medido); com a paleta anterior,
           que era sólida, seria o próprio --z2-bg-soft */
        --z2-head-bg: #2e2e2e;
        --z2-bg-hover: #99999915; /* antes #26282b */
        --z2-border: #99999920; /* antes #36383c */
        --z2-border-soft: #99999925; /* antes #2b2d30 */
        --z2-text: #e4e5e7;
        --z2-text-muted: #9da1a7;
        --z2-text-faint: #6c7076;
        --z2-accent: #2a9d6b;
        --z2-accent-strong: #178557;
        --z2-accent-hover: #1c9763;
        --z2-accent-soft: rgba(42, 157, 107, 0.15);
        --z2-accent-text: #62d29f;
        --z2-on-accent: #ffffff;
        --z2-checked-row: rgba(42, 157, 107, 0.07);
        --z2-selected-row: #99999925; /* antes rgba(255, 255, 255, 0.07) */
        --z2-popup: #303030; /* antes #26282b */
        --z2-shadow: 0 12px 32px rgba(0, 0, 0, 0.45);
        --z2-chip: #303030; /* antes #303236 */
        --z2-segment-bg: #99999920; /* antes #2a2c2f */
        --z2-segment-active: #363636; /* antes #3b3e43 */
        --z2-input: #99999910; /* antes #18191b */
        --z2-danger-bg: rgba(240, 82, 82, 0.16);
        --z2-danger-text: #f39c9c;
        --z2-alert-bg: rgba(230, 160, 40, 0.16);
        --z2-alert-text: #efc36b;
        --z2-success-bg: rgba(42, 157, 107, 0.16);
        --z2-success-text: #6fd3a3;
        --z2-blue-bg: rgba(59, 130, 246, 0.17);
        --z2-blue-text: #8fb9f7;
        --z2-purple-bg: rgba(139, 92, 246, 0.17);
        --z2-purple-text: #bca6f7;
        --z2-neutral-bg: rgba(255, 255, 255, 0.08);
        --z2-neutral-text: #c4c7cc;
    }
    /* as duas no :global inteiro, pelo mesmo motivo do bloco de tokens acima: a raiz é desenhada
       pelo Zblock. Com só o .theme_light global, o .zgrid2 continuava escopado e a regra não
       casava — o tema claro recebia as cores do escuro (faixa preta, texto cinza-claro no branco). */
    :global(.theme_light .zgrid2),
    :global(.zgrid2.is-paper) {
        --z2-bg: #ffffff;
        /* TESTE: cinzas do Zgrid original (css_theme_light.css). valor anterior do Zgrid2 comentado em cada linha */
        --z2-bg-soft: #f3f4f5; /* antes #f8f9fa — equivale ao #ffffffaa do cabeçalho antigo sobre o fundo do tema claro */
        --z2-head-bg: var(--z2-bg-soft); /* no claro a faixa já é sólida */
        --z2-bg-hover: #99999915; /* antes #f5f6f8 */
        --z2-border: #88888833; /* antes #e2e5e9 */
        --z2-border-soft: #99999925; /* antes #edeff2 */
        --z2-text: #1f2328;
        --z2-text-muted: #6b7280;
        --z2-text-faint: #a1a7af;
        --z2-accent: #178557;
        --z2-accent-strong: #157c53;
        --z2-accent-hover: #106744;
        --z2-accent-soft: #e9f5ef;
        --z2-accent-text: #0f6c45;
        --z2-on-accent: #ffffff;
        --z2-checked-row: #f5fbf8;
        --z2-selected-row: #99999925; /* antes #edf0f3 */
        --z2-popup: #ffffff;
        --z2-shadow: 0 12px 32px rgba(16, 24, 40, 0.12);
        --z2-chip: #dddddd; /* antes #eef0f2 */
        --z2-segment-bg: #99999920; /* antes #f0f1f3 */
        --z2-segment-active: #ffffff;
        --z2-input: #ffffff;
        --z2-danger-bg: #fdecec;
        --z2-danger-text: #c0352b;
        --z2-alert-bg: #fdf2df;
        --z2-alert-text: #98600f;
        --z2-success-bg: #e5f4ec;
        --z2-success-text: #14774a;
        --z2-blue-bg: #e8f0fe;
        --z2-blue-text: #2459c4;
        --z2-purple-bg: #f1ebfe;
        --z2-purple-text: #6d42c9;
        --z2-neutral-bg: #eff1f3;
        --z2-neutral-text: #4b5563;
    }

    /* ------------------------------------------------------------------ card */
    :global(.zgrid2) {
        /* fundo, borda, raio, recorte e a coluna flex vêm do Zblock (z/zblock.svelte), que é a
           casca deste componente. Aqui ficam só as coisas da tabela. */
        position: relative;
        font-size: 14px;
        color: var(--z2-text);
        /* o Zblock pinta o cartão com o cinza padrão; a tabela tem token próprio (muda no tema
           claro e na versão "papel" da página compartilhada) */
        background: var(--z2-bg);
        border-color: var(--z2-border);
    }
    .z2spacer {
        flex: 1 1 auto;
    }

    /* ------------------------------------------------------------------ botões */
    .z2btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        height: 34px;
        padding: 0 14px;
        font-size: 13px;
        font-weight: 500;
        white-space: nowrap;
        color: var(--z2-text);
        background: var(--z2-bg);
        border: 1px solid var(--z2-border);
        border-radius: 8px;
        cursor: pointer;
        transition:
            background-color 0.12s,
            border-color 0.12s;
    }
    .z2btn:hover:not(:disabled),
    .z2btn.is-open {
        background: var(--z2-bg-hover);
    }
    .z2btn:disabled {
        opacity: 0.5;
        cursor: default;
    }
    .z2btn--solid {
        color: var(--z2-on-accent);
        background: var(--z2-accent-strong);
        border-color: var(--z2-accent-strong);
        font-weight: 600;
    }
    .z2btn--solid:hover:not(:disabled) {
        background: var(--z2-accent-hover);
        border-color: var(--z2-accent-hover);
    }
    .z2btn--danger {
        color: var(--z2-danger-text);
        border-color: var(--z2-danger-text);
    }
    .z2btn--danger:hover:not(:disabled) {
        background: var(--z2-danger-bg);
    }
    .z2btn--warning {
        color: var(--z2-alert-text);
        border-color: var(--z2-alert-text);
    }
    .z2btn--warning:hover:not(:disabled) {
        background: var(--z2-alert-bg);
    }
    .z2btn--ghost {
        color: var(--z2-text-muted);
        background: transparent;
        border-color: transparent;
    }
    .z2btn--ghost:hover:not(:disabled) {
        color: var(--z2-text);
        background: transparent;
    }
    .z2caret {
        font-size: 9px;
        color: var(--z2-text-faint);
    }
    /* a Nunito (fonte do app) tem ascent bem maior que o descent: centralizado pela caixa, o texto fica ~1px acima do
       centro (medido no navegador: 1,25px no botão de 34px). o padding desce só o rótulo, sem mexer no ícone ao lado.
       todo texto de botão/item centralizado usa esta classe (na célula, o equivalente é .z2rbtn > span) */
    .z2label {
        padding-top: 2px;
    }
    .z2link {
        padding: 0;
        font-size: 13px;
        font-weight: 500;
        color: var(--z2-accent-text);
        background: transparent;
        border: 0;
        cursor: pointer;
    }
    .z2link:hover:not(:disabled) {
        text-decoration: underline;
    }
    .z2link:disabled {
        color: var(--z2-text-faint);
        cursor: default;
    }
    .z2link--muted {
        color: var(--z2-text-muted);
    }
    .z2icon_btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 30px;
        height: 30px;
        color: var(--z2-text-muted);
        background: transparent;
        border: 0;
        border-radius: 6px;
        cursor: pointer;
    }
    .z2icon_btn:hover {
        color: var(--z2-text);
        background: var(--z2-bg-hover);
    }
    .z2input {
        width: 100%;
        height: 34px;
        padding: 0 12px;
        font-size: 13px;
        color: var(--z2-text);
        background: var(--z2-input);
        border: 1px solid var(--z2-border);
        border-radius: 8px;
        outline: none;
    }
    .z2input::placeholder {
        color: var(--z2-text-faint);
    }
    .z2input:focus {
        border-color: var(--z2-accent);
        box-shadow: 0 0 0 3px var(--z2-accent-soft);
    }
    .z2check {
        width: 16px;
        height: 16px;
        margin: 0;
        vertical-align: middle;
        accent-color: var(--z2-accent-strong);
        cursor: pointer;
    }
    .z2check:disabled {
        cursor: default;
    }

    /* ------------------------------------------------------------------ cabeçalho */
    .z2head {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 12px;
        padding: 14px 16px;
        border-bottom: 1px solid var(--z2-border-soft);
    }
    /* sem título (showHeaders: false): só os controles, na altura da antiga barra */
    .z2head.is-bare {
        padding: 10px 16px;
    }
    .z2head_info {
        flex: 1 1 auto;
        min-width: 0;
    }
    .z2title {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 15px;
        font-weight: 600;
    }
    .z2count {
        /* sem o ajuste de .z2label: em algarismos pequenos ele passa do centro (medido no navegador) */
        padding: 1px 7px;
        font-size: 12px;
        font-weight: 600;
        color: var(--z2-text-muted);
        background: var(--z2-chip);
        border-radius: 6px;
        font-variant-numeric: tabular-nums;
    }
    .z2subtitle {
        margin-top: 2px;
        font-size: 13px;
        color: var(--z2-text-muted);
    }
    .z2head_acts {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: flex-end;
        gap: 8px;
    }

    /* ------------------------------------------------------------------ controles do grid (busca, Ações, Colunas)
       sem acts: na linha do título, à direita — margin-left: auto também os mantém à direita se quebrarem de linha
       numa janela estreita. com acts (e sempre no celular): linha própria, de borda a borda do card, com divisória */
    .z2controls {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 10px;
        margin-left: auto;
    }
    .z2head.has-acts .z2controls,
    .zgrid2.is-mobile .z2controls {
        flex: 0 0 calc(100% + 32px);
        justify-content: flex-end;
        margin: 2px -16px -14px;
        padding: 10px 16px;
        border-top: 1px solid var(--z2-border-soft);
    }
    /* sem título não há o que separar: os controles ficam sozinhos na linha, sem divisória */
    .zgrid2 .z2head.is-bare .z2controls {
        flex: 1 1 auto;
        margin: 0 0 0 auto;
        padding: 0;
        border-top: 0;
    }
    .z2controls_buttons {
        display: flex;
        align-items: center;
        gap: 8px;
    }
    .z2segments {
        display: inline-flex;
        gap: 2px;
        max-width: 100%;
        padding: 3px;
        overflow-x: auto;
        background: var(--z2-segment-bg);
        border-radius: 9px;
    }
    .z2segment {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 5px 12px;
        font-size: 13px;
        font-weight: 500;
        white-space: nowrap;
        color: var(--z2-text-muted);
        background: transparent;
        border: 0;
        border-radius: 7px;
        cursor: pointer;
    }
    .z2segment:hover:not(:disabled) {
        color: var(--z2-text);
    }
    .z2segment:disabled {
        opacity: 0.5;
        cursor: default;
    }
    .z2segment.is-active {
        font-weight: 600;
        color: var(--z2-text);
        background: var(--z2-segment-active);
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
    }
    .z2search {
        position: relative;
        width: 260px;
    }
    .z2search .z2input {
        padding-right: 32px;
    }
    .z2search_clear {
        position: absolute;
        top: 50%;
        right: 4px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 26px;
        height: 26px;
        font-size: 12px;
        color: var(--z2-text-faint);
        background: transparent;
        border: 0;
        border-radius: 6px;
        transform: translateY(-50%);
        cursor: pointer;
    }
    .z2search_clear:hover {
        color: var(--z2-text);
        background: var(--z2-bg-hover);
    }

    /* ------------------------------------------------------------------ seleção em massa */
    .z2selection {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px;
        padding: 8px 16px;
        background: var(--z2-accent-soft);
        border-bottom: 1px solid var(--z2-border-soft);
    }
    .z2selection_text {
        font-size: 13px;
        font-weight: 600;
        color: var(--z2-accent-text);
    }

    /* ------------------------------------------------------------------ caixas de resumo */
    .z2boxes {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        padding: 12px 16px;
        border-bottom: 1px solid var(--z2-border-soft);
    }
    .z2box {
        flex: 1 1 160px;
        padding: 10px 12px;
        background: var(--z2-bg-soft);
        border: 1px solid var(--z2-border-soft);
        border-radius: 8px;
    }
    .z2box_label {
        font-size: 12px;
        color: var(--z2-text-muted);
    }
    .z2box_value {
        margin-top: 2px;
        font-size: 18px;
        font-weight: 600;
        font-variant-numeric: tabular-nums;
    }

    /* ------------------------------------------------------------------ tabela */
    /* rolagem horizontal só quando a tabela não cabe (ver stickyHead): qualquer overflow aqui transformaria o corpo
       num contêiner de rolagem e prenderia o cabeçalho sticky — nesse caso a rolagem lateral tem prioridade */
    .z2body {
        width: 100%;
    }
    .z2body.is-scrollable {
        overflow-x: auto;
    }
    .z2table {
        width: 100%;
        border-collapse: collapse;
    }
    /* cabeçalho fixo ao rolar. top vem de --z2-sticky-top: 0, ou logo abaixo de uma barra Zact sticky da tela.
       a linha de baixo é box-shadow e não border: com border-collapse a borda não acompanha a célula sticky */
    .z2th {
        position: sticky;
        top: var(--z2-sticky-top, 0px);
        z-index: 2;
        padding: 10px 12px;
        font-size: 13px;
        font-weight: 500;
        text-align: left;
        white-space: nowrap;
        vertical-align: middle;
        color: var(--z2-text-muted);
        background: var(--z2-head-bg);
        box-shadow: inset 0 -1px 0 var(--z2-border-soft);
    }
    .z2th_inner {
        display: inline-flex;
        align-items: center;
        gap: 4px;
    }
    .z2th_sort {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 0;
        font: inherit;
        color: inherit;
        background: transparent;
        border: 0;
        cursor: pointer;
    }
    .z2th_sort:hover,
    .z2th_sort.is-sorted {
        color: var(--z2-text);
    }
    .z2sort_icon {
        font-size: 10px;
        color: var(--z2-text-faint);
        opacity: 0.7;
    }
    .z2th_sort:hover .z2sort_icon {
        opacity: 1;
    }
    .z2th_sort.is-sorted .z2sort_icon {
        color: var(--z2-accent);
        opacity: 1;
    }
    .z2th_filter {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 22px;
        height: 22px;
        padding: 0;
        font-size: 10px;
        color: var(--z2-text-faint);
        background: transparent;
        border: 0;
        border-radius: 5px;
        opacity: 0.7;
        cursor: pointer;
    }
    .z2th_filter:hover,
    .z2th_filter.is-open {
        color: var(--z2-text);
        background: var(--z2-chip);
        opacity: 1;
    }
    .z2th_filter.is-active {
        color: var(--z2-accent-text);
        background: var(--z2-accent-soft);
        opacity: 1;
    }
    .z2td {
        padding: 12px;
        vertical-align: middle;
        border-bottom: 1px solid var(--z2-border-soft);
        overflow-wrap: anywhere;
    }
    .z2table tr > :first-child {
        padding-left: 16px;
    }
    .z2table tr > :last-child {
        padding-right: 16px;
    }
    .z2th--check,
    .z2td--check {
        width: 44px;
        padding-right: 4px;
    }
    .z2td--buttons {
        white-space: nowrap;
    }
    .is-right {
        text-align: right;
    }
    .is-center {
        text-align: center;
    }
    .z2tr:hover > .z2td {
        background: var(--z2-bg-hover);
    }
    .z2tr.is-checked > .z2td {
        background: var(--z2-checked-row);
    }
    /* linha marcada: vem depois do hover e do checked, pra prevalecer sobre os dois */
    .z2tr.is-selected > .z2td {
        background: var(--z2-selected-row);
    }
    .z2tr.is-selected > .z2td:first-child {
        box-shadow: inset 3px 0 0 var(--z2-accent);
    }
    .z2tr.no-lines > .z2td {
        border-bottom-color: transparent;
    }
    .z2tr.is-removed > .z2td {
        opacity: 0.5;
    }
    .z2group td {
        padding-top: 8px;
        padding-bottom: 8px;
        font-size: 13px;
        font-weight: 600;
        color: var(--z2-text-muted);
        background: var(--z2-bg-soft);
        border-bottom: 1px solid var(--z2-border-soft);
    }
    .z2empty td {
        padding: 32px 16px;
        text-align: center;
        color: var(--z2-text-muted);
    }
    .z2total td {
        padding: 12px;
        font-weight: 600;
        background: var(--z2-bg-soft);
    }
    .z2total_value {
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
    }
    /* sem quebra: com uma coluna de total reordenada pra frente, o rótulo pode cair só na célula estreita do checkbox */
    .z2total_label {
        white-space: nowrap;
    }

    /* ------------------------------------------------------------------ rodapé */
    .z2foot {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 12px 16px;
        font-size: 13px;
        color: var(--z2-text-muted);
    }

    /* ------------------------------------------------------------------ popups (conteúdo) */
    .z2menu_title {
        padding: 10px 14px 8px;
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: var(--z2-text-faint);
        border-bottom: 1px solid var(--z2-border-soft);
    }
    .z2menu_list {
        flex: 1 1 auto;
        max-height: 320px;
        padding: 6px;
        overflow-y: auto;
    }
    .z2menu_item {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        width: 100%;
        padding: 8px;
        text-align: left;
        color: var(--z2-text);
        background: transparent;
        border: 0;
        border-radius: 7px;
        cursor: pointer;
    }
    .z2menu_item:hover:not(:disabled) {
        background: var(--z2-bg-hover);
    }
    .z2menu_item:disabled {
        opacity: 0.45;
        cursor: default;
    }
    .z2menu_item--compact {
        align-items: center;
        padding: 7px 10px;
        font-size: 13px;
    }
    .z2menu_item.is-danger {
        color: var(--z2-danger-text);
    }
    .z2menu_item.is-selected {
        font-weight: 600;
        color: var(--z2-accent-text);
    }
    .z2menu_item_check {
        margin-left: auto;
        font-size: 11px;
    }
    .z2menu_item_icon {
        width: 16px;
        font-size: 12px;
        text-align: center;
        color: var(--z2-text-muted);
    }
    .z2menu_item.is-danger .z2menu_item_icon {
        color: inherit;
    }
    .z2menu_icon {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 28px;
        height: 28px;
        font-size: 12px;
        color: var(--z2-text-muted);
        background: var(--z2-chip);
        border-radius: 6px;
    }
    .z2menu_text {
        display: flex;
        flex-direction: column;
        min-width: 0;
    }
    .z2menu_label {
        font-size: 13px;
        font-weight: 600;
    }
    .z2menu_hint {
        font-size: 12px;
        color: var(--z2-text-muted);
    }
    .z2menu_foot {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 9px 14px;
        font-size: 12px;
        color: var(--z2-text-muted);
        border-top: 1px solid var(--z2-border-soft);
    }
    .z2menu_empty {
        padding: 12px 8px;
        font-size: 13px;
        text-align: center;
        color: var(--z2-text-muted);
    }
    .z2option {
        display: flex;
        align-items: center;
        gap: 10px;
        margin: 0;
        padding: 7px 8px;
        font-size: 13px;
        border-radius: 6px;
        cursor: pointer;
    }
    .z2option:hover {
        background: var(--z2-bg-hover);
    }
    .z2option_label {
        flex: 1 1 auto;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    .z2option_count {
        font-size: 12px;
        color: var(--z2-text-faint);
        font-variant-numeric: tabular-nums;
    }
    .z2filter_head {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 10px 10px 10px 12px;
        border-bottom: 1px solid var(--z2-border-soft);
    }
    .z2filter_foot {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 10px 12px;
        border-top: 1px solid var(--z2-border-soft);
    }

    /* ------------------------------------------------------------------ popup de colunas */
    .z2colhead {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        padding: 12px 14px 10px;
        border-bottom: 1px solid var(--z2-border-soft);
    }
    .z2colhead_title {
        font-size: 14px;
        font-weight: 600;
    }
    /* position: relative -> é o offsetParent dos itens (o arraste mede posição por offsetTop) */
    .z2collist {
        position: relative;
        flex: 1 1 auto;
        max-height: 340px;
        padding: 6px;
        overflow-y: auto;
    }
    .z2collist.is-dragging {
        user-select: none;
        cursor: grabbing;
    }
    .z2colitem {
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 0 8px 0 2px;
        background: var(--z2-popup);
        border-radius: 7px;
    }
    .z2colitem:hover {
        background: var(--z2-bg-hover);
    }
    .z2colitem.is-dragging {
        position: relative;
        z-index: 1;
        background: var(--z2-bg-hover);
        box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
    }
    .z2grip {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 22px;
        height: 30px;
        padding: 0;
        font-size: 11px;
        color: var(--z2-text-faint);
        background: transparent;
        border: 0;
        border-radius: 5px;
        cursor: grab;
        touch-action: none;
    }
    .z2grip:hover,
    .z2grip:focus-visible {
        color: var(--z2-text-muted);
    }
    .z2collist.is-dragging .z2grip {
        cursor: grabbing;
    }
    .z2colitem_option {
        display: flex;
        align-items: center;
        gap: 10px;
        flex: 1 1 auto;
        min-width: 0;
        margin: 0;
        padding: 7px 0;
        font-size: 13px;
        cursor: pointer;
    }
    .z2colitem_option.is-fixed {
        color: var(--z2-text-faint);
        cursor: default;
    }
    .z2colitem_fixed {
        flex-shrink: 0;
        font-size: 12px;
        color: var(--z2-text-faint);
    }
    .z2colfoot_count {
        font-size: 12px;
        color: var(--z2-text-muted);
    }

    /* ------------------------------------------------------------------ mobile (cards) */
    /* no celular os acts caem numa linha só deles, abaixo do título */
    .zgrid2.is-mobile .z2head {
        flex-wrap: wrap;
    }
    .zgrid2.is-mobile .z2head_info {
        flex-basis: 100%;
    }
    .zgrid2.is-mobile .z2head_acts {
        width: 100%;
        justify-content: flex-start;
    }
    .zgrid2.is-mobile .z2controls {
        flex-direction: column;
        align-items: stretch;
    }
    .zgrid2.is-mobile .z2search {
        width: 100%;
    }
    .zgrid2.is-mobile .z2controls_buttons > * {
        flex: 1 1 auto;
        justify-content: center;
    }
    .zgrid2.is-mobile .z2spacer {
        display: none;
    }
    .z2card {
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 12px 16px;
        border-bottom: 1px solid var(--z2-border-soft);
    }
    .z2card.is-removed {
        opacity: 0.5;
    }
    .z2card.is-selected {
        background: var(--z2-selected-row);
        box-shadow: inset 3px 0 0 var(--z2-accent);
    }
    .z2card_field {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 12px;
    }
    .z2card_label {
        flex-shrink: 0;
        padding-top: 1px;
        font-size: 12px;
        color: var(--z2-text-muted);
    }
    .z2card_value {
        min-width: 0;
        text-align: right;
        overflow-wrap: anywhere;
    }
    .z2card_buttons {
        display: flex;
        justify-content: flex-end;
    }
    .z2group_card {
        padding: 8px 16px;
        font-size: 13px;
        font-weight: 600;
        color: var(--z2-text-muted);
        background: var(--z2-bg-soft);
        border-bottom: 1px solid var(--z2-border-soft);
    }
    .z2empty_card {
        padding: 32px 16px;
        text-align: center;
        color: var(--z2-text-muted);
    }

    /* ------------------------------------------------------------------ impressão (Ctrl+P na própria tela)
       a impressão pelo menu já recebe uma cópia estática (snapshotGrid) — aqui é só pro caso de imprimir a tela. */
    @media print {
        :global(.zgrid2) {
            --z2-bg: #ffffff;
            --z2-bg-soft: #f8f9fa;
            --z2-head-bg: #f8f9fa;
            --z2-bg-hover: #ffffff;
            --z2-border: #e2e5e9;
            --z2-border-soft: #edeff2;
            --z2-text: #1f2328;
            --z2-text-muted: #6b7280;
            --z2-text-faint: #a1a7af;
            --z2-accent: #178557;
            --z2-accent-text: #0f6c45;
            --z2-checked-row: #ffffff;
            --z2-selected-row: #ffffff;
            --z2-chip: #eef0f2;
            --z2-danger-bg: #fdecec;
            --z2-danger-text: #c0352b;
            --z2-alert-bg: #fdf2df;
            --z2-alert-text: #98600f;
            --z2-success-bg: #e5f4ec;
            --z2-success-text: #14774a;
            --z2-blue-bg: #e8f0fe;
            --z2-blue-text: #2459c4;
            --z2-purple-bg: #f1ebfe;
            --z2-purple-text: #6d42c9;
            --z2-neutral-bg: #eff1f3;
            --z2-neutral-text: #4b5563;
        }
        .z2tr.is-selected > .z2td:first-child,
        .z2card.is-selected {
            box-shadow: none;
        }
        /* no papel não existe rolagem: o corpo tem que soltar o conteúdo, senão o que passa da largura da folha
           sai cortado e o navegador ainda desenha a barra no meio da página. o !important é contra o
           `body * { overflow-y: hidden !important }` do css_print.css: com um eixo escondido, o outro vira
           rolagem e o corte volta */
        .z2body,
        .z2body.is-scrollable {
            overflow: visible !important;
        }
        /* a largura mínima que a tela usa para poder rolar de lado é maior que a folha: no papel ela empurra as
           últimas colunas para fora da margem. !important porque o valor chega pelo style do elemento (grid.minWidth) */
        .z2table {
            min-width: 0 !important;
        }
        /* no papel o cabeçalho não gruda: o navegador já repete o thead em cada página.
           e pode quebrar linha: é o que deixa a tabela encolher até caber na folha em vez de transbordar */
        .z2th {
            position: static;
            white-space: normal;
        }
        .z2head_acts,
        .z2controls,
        .z2selection,
        .z2foot,
        .z2th_filter,
        .z2sort_icon,
        .z2th--check,
        .z2td--check,
        .z2th--buttons,
        .z2td--buttons,
        .z2boxes {
            display: none;
        }
        /* linhas baixas pra não consumir folhas: o Zgrid antigo imprimia com 2px em cima e embaixo nas células,
           mantendo a fonte da tela. o espaçamento de leitura em tela não serve pro papel */
        .z2td,
        .z2th,
        .z2total td,
        .z2group td {
            padding: 2px 6px;
            line-height: 1.25;
        }
        /* fonte menor no papel: com a da tela, colunas como data e identificador quebram em duas linhas e cada
           linha da tabela dobra de altura. a regra vai na tabela porque a tela pode fixar o tamanho no card */
        .z2table {
            font-size: 11px;
        }
        .z2th {
            font-size: 10px;
        }
        .z2table tr > :first-child {
            padding-left: 0;
        }
        .z2table tr > :last-child {
            padding-right: 0;
        }
        .z2head {
            padding: 0 0 6px;
            border-bottom: 0;
        }
        .z2title {
            font-size: 1em;
        }
        .z2subtitle {
            font-size: 0.85em;
        }
        .z2count {
            padding: 0;
            background: none;
        }
        .z2empty td {
            padding: 8px 0;
        }
    }
</style>
